import { normalizeSafeAddress } from "../utils/safe-types.mjs";
//#region src/client/query-filters.ts
/**
* Default page size applied when `limit` is omitted, so every transport
* requests the same page size instead of falling back to differing server
* defaults.
*/
const DEFAULT_PAGE_SIZE = 50;
/**
* Validates pagination bounds and resolves the traversal direction.
*
* `after` and `before` are exclusive ledger-position bounds. Until every
* transport can combine bounds freely, a query accepts at most one bound, and
* the bound must match the traversal direction (`after` implies ascending,
* `before` implies descending).
*/
function resolvePagination(options) {
	if (options.after != null && options.before != null) throw new Error("Only one of `after` or `before` may be provided");
	const descending = options.order ? options.order === "descending" : options.before != null;
	if (options.after != null && descending) throw new Error("`after` can not be combined with descending queries");
	if (options.before != null && !descending) throw new Error("`before` can not be combined with ascending queries");
	return {
		descending,
		after: options.after ?? void 0,
		before: options.before ?? void 0,
		limit: options.limit ?? DEFAULT_PAGE_SIZE
	};
}
/**
* Validates that a transaction query's filter supports pagination bounds.
*
* Function filters can only be paginated when fully qualified, since JSON-RPC
* rejects cursors on package- or module-level `MoveFunction` filters.
*/
function validateTransactionQuery(filter, pagination) {
	if (filter?.$kind === "function" && (pagination.after != null || pagination.before != null) && (!filter.module || !filter.function)) throw new Error("Paginating transactions filtered by function requires a fully qualified `package::module::function`");
}
/**
* Validates a transaction filter and resolves any MVR names it contains.
*
* Every transport resolves filters through this function so that validation
* errors and MVR handling are identical across clients.
*/
async function resolveTransactionFilter(mvr, filter, signal) {
	assertSinglePredicate(filter, ["sender", "function"], "transaction");
	if (filter.sender != null) return {
		$kind: "sender",
		sender: normalizeSafeAddress(filter.sender)
	};
	const [pkg, module, fn, ...rest] = filter.function.split("::");
	if (!pkg || rest.length > 0 || module === "" || fn === "") throw new Error(`Invalid function filter "${filter.function}": expected "package", "package::module", or "package::module::function"`);
	return {
		$kind: "function",
		package: normalizeSafeAddress((await mvr.resolvePackage({
			package: pkg,
			signal
		})).package),
		module,
		function: fn
	};
}
/**
* Validates an event filter and resolves any MVR names it contains.
*
* `emitModule` must name a module (`package::module`), and `eventType` must be
* at least module-qualified (`package::module` or a full type). Every transport
* resolves filters through this function so that validation errors and MVR
* handling are identical across clients.
*/
async function resolveEventFilter(mvr, filter, signal) {
	assertSinglePredicate(filter, [
		"sender",
		"emitModule",
		"eventType"
	], "event");
	if (filter.sender != null) return {
		$kind: "sender",
		sender: normalizeSafeAddress(filter.sender)
	};
	if (filter.emitModule != null) {
		const [pkg, module, ...rest] = filter.emitModule.split("::");
		if (!pkg || !module || rest.length > 0) throw new Error(`Invalid emitModule filter "${filter.emitModule}": expected "package::module"`);
		return {
			$kind: "emitModule",
			package: normalizeSafeAddress((await mvr.resolvePackage({
				package: pkg,
				signal
			})).package),
			module
		};
	}
	const eventType = filter.eventType;
	const segments = eventType.split("::");
	if (segments.length === 2 && segments.every((segment) => segment !== "")) return {
		$kind: "eventTypeModule",
		package: normalizeSafeAddress((await mvr.resolvePackage({
			package: segments[0],
			signal
		})).package),
		module: segments[1]
	};
	if (segments.length < 3 || segments.some((segment) => segment === "")) throw new Error(`Invalid eventType filter "${eventType}": expected "package::module" or a fully qualified type name`);
	return {
		$kind: "eventType",
		eventType: (await mvr.resolveType({
			type: eventType,
			signal
		})).type
	};
}
function assertSinglePredicate(filter, predicates, kind) {
	const present = predicates.filter((predicate) => filter[predicate] != null);
	if (present.length !== 1) throw new Error(`A ${kind} filter must specify exactly one of ${predicates.join(", ")}${present.length ? ` (got ${present.join(", ")})` : ""}`);
}
//#endregion
export { resolveEventFilter, resolvePagination, resolveTransactionFilter, validateTransactionQuery };

//# sourceMappingURL=query-filters.mjs.map